import { DateTime } from 'luxon'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Form, Grid, Image, ListItem, StatusBadge, Text,
} from '@domain-group/suit-theme'

import TaskIcon from '@components/TaskIcon'

import useOrderItemTask from '@hooks/useOrderItemTask'
import useOrderItemTaskAttachments from '@hooks/useOrderItemTaskAttachments'

import type { OrderItemTaskModel } from '@models/types'

interface OrderItemTaskProps {
  orderItemTask: OrderItemTaskModel,
}

const Task = (props: OrderItemTaskProps) => {
  const {
    orderItemTask, orderItemTask: {
      due_date, id, instructions, notes, order_item_task_type_id,
    },
  } = props

  const taskPayload = useOrderItemTask(orderItemTask)
  const {
    callbacks: { createOrEditOrderItemTask },
    isPastDueDate,
    status: { title: statusTitle },
    taskColor,
    orderItemTaskType: { title: taskTypeTitle },
  } = taskPayload

  const orderItemTaskAttachmentsPayload = useOrderItemTaskAttachments(
    { filters: { order_item_task_id: id } },
  )

  const {
    filteredOrderItemTaskAttachments,
    hasFilteredOrderItemTaskAttachments,
  } = orderItemTaskAttachmentsPayload

  const completedDate = digObject(orderItemTask, 'completed_date.date_time', undefined)
  const parsedCompletedDate = DateTime.fromFormat(completedDate, 'dd/MM/yyyy hh:mm')

  return (

    <Box border="1px solid" borderColor="lineColor" borderRadius={5} flexDirection="column" marginBottom="large">
      <ListItem
        boxProps={{
          fontSize: 'small',
          padding: 'large',
        }}
        flexDirection="column"
        forceBottomBorder
        onClick={() => createOrEditOrderItemTask()}
        showAngleIcon
      >
        <Box alignItems="center">
          <Box marginRight="large" width="auto">
            <TaskIcon taskTypeId={order_item_task_type_id} variant="light" />
          </Box>

          <Box flexDirection="column" width="auto">
            <Text color={`#${taskColor}`}>{taskTypeTitle}</Text>
            <Text
              color={isPastDueDate ? 'red' : 'bodyFontLightColor'}
              fontSize="xsmall"
              lineHeight="1.3"
              marginTop="medium"
              marginRight="large"
              style={{ flexShrink: '0', width: '150px' }}
            >
              {completedDate
                ? `Completed ${parsedCompletedDate.toFormat('dd/MM/yyyy')}`
                : `${due_date ? `Due ${due_date}` : 'Due Date Not Set'}`}
            </Text>
          </Box>

          <Box marginLeft="auto" width="auto">
            <StatusBadge color={`#${taskColor}`} ghost text={statusTitle} />
          </Box>
        </Box>
      </ListItem>

      {!!instructions && (
        <Form.Field label="Instructions" boxProps={{ borderTop: '1px dashed', borderColor: 'lineColor', padding: 'large' }}>
          <Text fontSize="xsmall" lineHeight="1.3">
            {instructions}
          </Text>
        </Form.Field>
      )}

      {!!completedDate && (
        <Box flexDirection="column" paddingBottom="large" paddingX="large">
          {hasFilteredOrderItemTaskAttachments && (
            <Grid
              // @ts-expect-error TS2322
              gridColumnGap="small"
              gridRowGap="small"
              gridTemplateColumns="repeat(auto-fill, minmax(90px, 1fr))"
              gridTemplateRows="repeat(auto-fill, minmax(90px, 1fr))"
              width="auto"
            >
              {filteredOrderItemTaskAttachments.map(attachment => (
                <Image
                  borderRadius={5}
                  height={90}
                  // @ts-expect-error TS2322
                  id={attachment.id}
                  key={attachment.id}
                  url={digObject(attachment, 'size.thumb')}
                  width="auto"
                />
              ))}
            </Grid>
          )}

          <Form.Field label="Completed" marginTop="large">
            <Text fontSize="xsmall" lineHeight="1.3">
              {parsedCompletedDate.toFormat("dd/MM/yyyy 'at' h:mma")}
            </Text>
          </Form.Field>

          {notes && (
            <Form.Field label="Notes" marginTop="large">
              <Text fontSize="xsmall" lineHeight="1.3">
                {notes}
              </Text>
            </Form.Field>
          )}
        </Box>
      )}
    </Box>
  )
}

export default Task
